import React from 'react'
import { intl } from 'lang/intl'

import {
  TtTrayChyronSpeaker,
  TtDropdown,
  TtMenu,
  TtMenuItem,
  TtButton,
  TtIcon,
} from '@23/ui-react'

import {
  useOverlayState,
  setSpeakerInfoChyron,
  removeOverlay,
} from '../../../../firebase/hooks/useOverlays'
import { useVisibleWebinarSpeakers } from '../../../../sharedLegacy/hooks/useSpeakersQuery'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  setActiveCard,
  updateChyronSpeakerCard,
  useTrayCard,
  useTrayCardState,
} from '../../../../firebase/hooks/useTray'
import { type PropsWithTrayItemChildren } from '../TrayItem'

import './ChyronSpeakerCard.scss'

interface ChyronSpeakerCardProps {
  id: string
}

const ChyronSpeakerCard = ({
  id,
  children,
}: PropsWithTrayItemChildren<ChyronSpeakerCardProps>) => {
  const card = useTrayCard(id)
  const cardState = useTrayCardState(id)

  const speakers = useVisibleWebinarSpeakers()
  const overlay = useOverlayState()

  if (card?.type !== 'chyron-speaker') return null

  const speaker = speakers.find(
    (s) => card.speakerId && s.id === +card.speakerId
  )

  const isChyronShowing =
    overlay?.type === ChyronType.SpeakerInfo &&
    overlay.liveSpeakerId === speaker?.id

  const showChyron = () => {
    if (speaker) void setSpeakerInfoChyron(speaker)
  }

  return (
    <TtTrayChyronSpeaker
      tag={intl.formatMessage({ defaultMessage: 'Speaker' })}
      name={
        !speaker
          ? intl.formatMessage({ defaultMessage: 'Name' })
          : speaker.fullName
      }
      jobTitle={
        !speaker
          ? intl.formatMessage({ defaultMessage: 'Title' })
          : speaker.title
      }
      company={
        !speaker
          ? intl.formatMessage({ defaultMessage: 'Company' })
          : speaker.company
      }
      bio={speaker?.bio}
      imageUrl={speaker?.avatarURL}
      selected={cardState === 'active'}
      disabled={cardState === 'done'}
      onTtClick={(event) => {
        if (event.target === event.currentTarget) {
          if (speaker) {
            showChyron()
            setActiveCard(id)
          }
        }
      }}
    >
      {card.speakerId && (
        <TtButton
          slot="toolbar"
          label={
            isChyronShowing
              ? intl.formatMessage(
                  { defaultMessage: 'Show speaker TvCard for {speakerName}' },
                  { speakerName: speaker.fullName }
                )
              : intl.formatMessage(
                  { defaultMessage: 'Hide speaker TvCard for {speakerName}' },
                  { speakerName: speaker?.fullName }
                )
          }
          color="secondary"
          variant="ghost"
          size="x-small"
          circle
          onClick={isChyronShowing ? removeOverlay : showChyron}
        >
          <TtIcon
            library="one/webinars"
            name={isChyronShowing ? 'visibility-off' : 'visibility-on'}
          />
        </TtButton>
      )}

      {speakers.length > 0 && (
        <TtDropdown
          className="chyron-speaker-card-speaker-selector"
          placement="bottom-start"
          closeOnSelect
          hoist
          slot="toolbar"
        >
          <TtButton
            slot="trigger"
            color="secondary"
            variant="ghost"
            size="x-small"
            circle
            label={intl.formatMessage({
              defaultMessage: 'Choose a speaker for the TvCard',
            })}
          >
            <TtIcon library="one/webinars" name="speaker" />
          </TtButton>
          <TtMenu
            onTtSelect={(e) => updateChyronSpeakerCard(id, e.detail.item.value)}
          >
            {speakers.map((s) => (
              <TtMenuItem key={s.id} value={`${s.id}`}>
                {s.fullName}
              </TtMenuItem>
            ))}
          </TtMenu>
        </TtDropdown>
      )}

      {children?.()}
    </TtTrayChyronSpeaker>
  )
}

export { ChyronSpeakerCard }
